import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import API from '@/lib/api';
import SignaturePad from '@/components/SignaturePad';
import { X, DeviceTablet, DeviceMobile, Copy, Check, WhatsappLogo, EnvelopeSimple, ChatCircle } from '@phosphor-icons/react';

/**
 * QuickSignModal — laat de gebruiker een leverbon laten tekenen, ofwel direct op een
 * tablet, ofwel door een publieke teken-link te versturen die de klant scant met zijn smartphone.
 *
 * Props:
 *  - docId: string (leverbon-id)
 *  - klantNaam: string
 *  - klantGsm, klantEmail: optioneel — maakt share-knoppen actief
 *  - onClose: () => void
 *  - onSigned: () => void — callback bij succesvol tekenen op tablet
 */
export default function QuickSignModal({ docId, klantNaam = '', klantGsm = '', klantEmail = '', onClose, onSigned }) {
  const [tab, setTab] = useState('link'); // 'link' | 'tablet'
  const [link, setLink] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const genLink = async () => {
    setLinkLoading(true);
    setErr('');
    try {
      const { data } = await API.post(`/documenten/${docId}/teken-link`);
      const url = `${window.location.origin}/lever-tekenen/${data.token}`;
      setLink({ url, expires: data.expires });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Link aanmaken mislukt');
    } finally {
      setLinkLoading(false);
    }
  };

  const kopieer = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Kopieer deze link:', link.url);
    }
  };

  const signOnTablet = async ({ dataUrl, naam }) => {
    setBusy(true);
    setErr('');
    try {
      await API.post(`/documenten/${docId}/handtekening`, { handtekening: dataUrl, naam });
      onSigned?.();
      onClose?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Ondertekenen mislukt');
    } finally {
      setBusy(false);
    }
  };

  const bericht = link ? `Beste ${klantNaam || 'klant'}, gelieve de leveringsbon digitaal te ondertekenen via deze beveiligde link: ${link.url}` : '';
  const waLink = link && klantGsm ? `https://wa.me/${klantGsm.replace(/[^0-9+]/g, '')}?text=${encodeURIComponent(bericht)}` : '';
  const smsLink = link && klantGsm ? `sms:${klantGsm}${/android/i.test(navigator.userAgent) ? '?' : '&'}body=${encodeURIComponent(bericht)}` : '';
  const mailLink = link && klantEmail ? `mailto:${klantEmail}?subject=${encodeURIComponent(`Digitaal ondertekenen ${docId}`)}&body=${encodeURIComponent(bericht)}` : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="quick-sign-modal">
      <div className="w-full max-w-2xl mx-4 bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Leverbon laten ondertekenen</h3>
            <p className="text-[11px] text-gray-500">{docId}{klantNaam && ` · ${klantNaam}`}</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="qs-close"><X size={18} /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          <button
            onClick={() => setTab('link')}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition ${tab === 'link' ? 'bg-white text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-500 hover:bg-white/60'}`}
            data-testid="qs-tab-link"
          >
            <DeviceMobile size={16} weight="bold" /> Smartphone-link + QR
          </button>
          <button
            onClick={() => setTab('tablet')}
            className={`flex-1 px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition ${tab === 'tablet' ? 'bg-white text-[#002FA7] border-b-2 border-[#002FA7]' : 'text-gray-500 hover:bg-white/60'}`}
            data-testid="qs-tab-tablet"
          >
            <DeviceTablet size={16} weight="bold" /> Tekenen op tablet
          </button>
        </div>

        {err && <div className="px-5 py-2 bg-red-50 border-b border-red-200 text-xs text-red-700 font-bold">{err}</div>}

        {/* Body */}
        <div className="p-5">
          {tab === 'link' ? (
            !link ? (
              <div className="text-center py-4">
                <p className="text-sm text-gray-600 mb-3">
                  Genereer een unieke teken-link. De klant scant de QR met zijn smartphone of klikt op de link om digitaal te ondertekenen — zonder account. De link blijft geldig tot de leverbon effectief ondertekend wordt.
                </p>
                <button
                  onClick={genLink}
                  disabled={linkLoading}
                  className="btn btn-primary inline-flex items-center gap-2"
                  data-testid="qs-genlink"
                >
                  {linkLoading ? 'Bezig...' : 'Genereer teken-link'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* QR + link */}
                <div className="flex flex-col items-center p-4 border border-amber-300 bg-amber-50">
                  <QRCodeSVG value={link.url} size={180} level="M" includeMargin={false} data-testid="qs-qr" />
                  <p className="mt-3 text-[10px] text-amber-800 font-bold uppercase tracking-wider">Scan om te ondertekenen</p>
                  <p className="mt-1 text-[10px] text-gray-600 italic">Vervalt na ondertekening</p>
                </div>
                {/* Share options */}
                <div className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Publieke link</p>
                  <div className="flex items-center gap-1">
                    <input
                      readOnly
                      value={link.url}
                      className="field-input text-[11px] font-mono flex-1"
                      onFocus={e => e.target.select()}
                      data-testid="qs-link-input"
                    />
                    <button onClick={kopieer} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="qs-copy">
                      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} weight="bold" />}
                      {copied ? 'Kopieerd!' : 'Kopieer'}
                    </button>
                  </div>

                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 pt-2">Direct versturen</p>
                  <div className="flex flex-wrap gap-1.5">
                    {klantGsm && (
                      <a href={waLink} target="_blank" rel="noreferrer" className="btn btn-sm inline-flex items-center gap-1 bg-[#25D366] text-white hover:brightness-95" data-testid="qs-share-wa">
                        <WhatsappLogo size={14} weight="fill" /> WhatsApp
                      </a>
                    )}
                    {klantGsm && (
                      <a href={smsLink} className="btn btn-sm btn-secondary inline-flex items-center gap-1" data-testid="qs-share-sms">
                        <ChatCircle size={14} weight="bold" /> SMS
                      </a>
                    )}
                    {klantEmail && (
                      <a href={mailLink} className="btn btn-sm btn-secondary inline-flex items-center gap-1" data-testid="qs-share-email">
                        <EnvelopeSimple size={14} weight="bold" /> E-mail
                      </a>
                    )}
                    {!klantGsm && !klantEmail && (
                      <p className="text-[10px] text-gray-500 italic">Geen GSM of e-mail bekend bij deze klant — kopieer de link manueel.</p>
                    )}
                  </div>

                  <div className="pt-2 text-[10px] text-gray-500 leading-relaxed border-t border-gray-200 mt-2">
                    <strong className="text-gray-700">Wat ziet de klant?</strong><br />
                    Een eenvoudige beveiligde pagina waar hij zijn naam invult en met de vinger een handtekening zet. Bij ondertekening krijgt u de leverbon terug + automatisch status "geleverd".
                  </div>
                </div>
              </div>
            )
          ) : (
            <div>
              <p className="text-sm text-gray-600 mb-3 text-center">Geef de tablet/telefoon aan de klant. Hij tekent hieronder met zijn vinger:</p>
              <div className="flex justify-center">
                <SignaturePad
                  width={520}
                  height={200}
                  initialName={klantNaam}
                  onSave={signOnTablet}
                  onCancel={onClose}
                />
              </div>
              {busy && <div className="text-center text-xs text-gray-500 mt-2">Bezig met opslaan...</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
